import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { MyNursery, MyNurseryChild, NurseryRoom } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, Field } from '../../shared/ui/components';
import { CameraViewer } from './CameraViewer';
import { CARE_LABEL, clock, formatAge } from './format';
import { QrCode } from './QrCode';

/** Lo que ve un papá o una mamá: sus hijos, su ficha, quién puede recogerlos y la estancia de hoy. */
export function GuardianView({ churchId, data, onChange }: { churchId: string; data: MyNursery; onChange: () => void }) {
  if (data.children.length === 0) {
    return (
      <EmptyState title="Aún no tienes hijos registrados.">
        Agrégalos en <Link to="/app/perfil">tu perfil</Link>, dentro de tu familia. Después podrás inscribirlos aquí.
      </EmptyState>
    );
  }
  return (
    <>
      {data.children.map((child) => (
        <ChildCard key={child.personId} churchId={churchId} child={child} rooms={data.rooms} onChange={onChange} />
      ))}
      {data.history.length > 0 ? (
        <Card title="Estancias anteriores">
          <ul className="list">
            {data.history.map((h) => (
              <li key={h.id} className="list__item">
                <span>
                  <strong>{h.childName}</strong> <span className="muted small">· {h.roomName}</span>
                </span>
                <span className="small muted">
                  {new Date(h.checkedInAt).toLocaleDateString('es', { day: 'numeric', month: 'short' })} {clock(h.checkedInAt)}–{clock(h.checkedOutAt)} · entregado a {h.pickedUpBy}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </>
  );
}

function ChildCard({ churchId, child, rooms, onChange }: { churchId: string; child: MyNurseryChild; rooms: NurseryRoom[]; onChange: () => void }) {
  const [editing, setEditing] = useState(!child.registered);
  const title = (
    <>
      {child.number ? <span className="badge">#{child.number}</span> : null} {child.name} {child.ageMonths !== null ? <span className="muted small">· {formatAge(child.ageMonths)}</span> : null}
    </>
  );
  return (
    <Card title={title} action={child.stay ? <span className="badge badge--active">En {child.stay.room.name}</span> : null}>
      {child.stay ? <StayPanel churchId={churchId} child={child} /> : null}
      {!child.registered && child.ageMonths !== null && child.ageMonths >= 36 ? (
        <p className="muted small">La guardería es solo para menores de 3 años.</p>
      ) : editing ? (
        <ProfileForm churchId={churchId} child={child} onSaved={() => { setEditing(false); onChange(); }} onCancel={child.registered ? () => setEditing(false) : null} />
      ) : (
        <>
          <dl className="details">
            <dt>Alergias</dt>
            <dd className={child.allergies ? 'text-error' : undefined}>{child.allergies ?? 'Ninguna'}</dd>
            <dt>Cuidados especiales</dt>
            <dd>{child.instructions ?? '—'}</dd>
            <dt>Medicamentos o condiciones</dt>
            <dd>{child.medical ?? '—'}</dd>
            <dt>Teléfono de contacto</dt>
            <dd>{child.contactPhone ?? '—'}</dd>
            <dt>Pañal</dt>
            <dd>{child.diaper ? 'Sí' : 'No'}</dd>
            <dt>Fotos o video</dt>
            <dd>{child.photoConsent ? 'Con permiso' : 'Sin permiso'}</dd>
          </dl>
          <Pickups churchId={churchId} child={child} onChange={onChange} />
          <div className="actions actions--wrap">
            {!child.stay ? <CheckIn churchId={churchId} child={child} rooms={rooms} onDone={onChange} /> : null}
            <Button type="button" variant="ghost" onClick={() => setEditing(true)}>
              Editar ficha
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

function StayPanel({ churchId, child }: { churchId: string; child: MyNurseryChild }) {
  const stay = child.stay!;
  return (
    <div className="stay">
      <div className="stay__code">
        <QrCode value={stay.code} label={`Código de entrega de ${child.name}`} />
        <div>
          <p className="small muted">Código para recogerlo</p>
          <p className="stay__code-text">{stay.code}</p>
          <p className="small muted">
            Desde las {clock(stay.checkedInAt)} · lo entregó {stay.droppedOffBy}
          </p>
        </div>
      </div>
      <p className="small muted">El código no basta: al recogerlo, el personal verificará tu identificación.</p>
      {stay.events.length > 0 ? (
        <ul className="timeline">
          {stay.events.map((e) => (
            <li key={e.id} className={e.kind === 'incident' || e.kind === 'call_guardian' ? 'timeline__item timeline__item--urgent' : 'timeline__item'}>
              <strong>{CARE_LABEL[e.kind]}</strong> <span className="muted small">· {clock(e.recordedAt)} · {e.recordedBy}</span>
              {e.note ? <p className="small">{e.note}</p> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {stay.cameras.map((c) => (
        <CameraViewer key={c.id} churchId={churchId} cameraId={c.id} name={c.name} />
      ))}
    </div>
  );
}

function ProfileForm({ churchId, child, onSaved, onCancel }: { churchId: string; child: MyNurseryChild; onSaved: () => void; onCancel: (() => void) | null }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ids = { allergies: `allergies-${child.personId}`, instructions: `instructions-${child.personId}` };

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.put(`/nursery/churches/${churchId}/children/${child.personId}`, {
        allergies: String(form.get('allergies') ?? ''),
        instructions: String(form.get('instructions') ?? ''),
        medical: String(form.get('medical') ?? ''),
        contactPhone: String(form.get('contactPhone') ?? ''),
        diaper: form.get('diaper') === 'on',
        photoConsent: form.get('photoConsent') === 'on',
      });
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={save}>
      {!child.registered ? <p className="muted small">Inscríbelo en la guardería de esta iglesia. Esta ficha solo la ve el personal de guardería de aquí.</p> : null}
      <div className="field">
        <label htmlFor={ids.allergies}>Alergias</label>
        <input id={ids.allergies} name="allergies" maxLength={500} defaultValue={child.allergies ?? ''} placeholder="Ninguna" />
      </div>
      <div className="field">
        <label htmlFor={ids.instructions}>Cuidados especiales</label>
        <textarea id={ids.instructions} name="instructions" maxLength={1000} rows={3} defaultValue={child.instructions ?? ''} />
      </div>
      <Field label="Medicamentos o condiciones médicas" name="medical" maxLength={500} defaultValue={child.medical ?? ''} placeholder="Ninguno" />
      <Field label="Teléfono de contacto durante el servicio" name="contactPhone" type="tel" maxLength={30} defaultValue={child.contactPhone ?? ''} />
      <label className="check">
        <input type="checkbox" name="diaper" defaultChecked={!!child.diaper} /> Usa pañal
      </label>
      <label className="check">
        <input type="checkbox" name="photoConsent" defaultChecked={!!child.photoConsent} /> Doy permiso de que la guardería le tome fotos o video
      </label>
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions actions--wrap">
        <Button type="submit" busy={busy}>
          {child.registered ? 'Guardar ficha' : 'Inscribir'}
        </Button>
        {onCancel ? (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        ) : null}
      </div>
    </form>
  );
}

function Pickups({ churchId, child, onChange }: { churchId: string; child: MyNurseryChild; onChange: () => void }) {
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const base = `/nursery/churches/${churchId}/children/${child.personId}/pickups`;

  async function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy('add');
    setError(null);
    try {
      await api.post(base, { name: String(form.get('name')), relationship: String(form.get('relationship')) });
      setAdding(false);
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function revoke(id: string) {
    setBusy(id);
    setError(null);
    try {
      await api.delete(`${base}/${id}`);
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="pickups">
      <h3 className="small">También pueden recogerlo</h3>
      {child.pickups.length === 0 ? <p className="muted small">Solo los tutores de la familia.</p> : null}
      <ul className="list">
        {child.pickups.map((p) => (
          <li key={p.id} className="list__item">
            <span>
              {p.name} <span className="muted small">· {p.relationship}</span>
            </span>
            <Button type="button" variant="ghost" busy={busy === p.id} onClick={() => { if (window.confirm(`¿Quitar a ${p.name}? Ya no podrá recoger a ${child.name}.`)) revoke(p.id); }} aria-label={`Quitar a ${p.name}`}>
              Quitar
            </Button>
          </li>
        ))}
      </ul>
      {adding ? (
        <form className="form form--inline" onSubmit={add}>
          <div className="field">
            <label htmlFor={`pickup-name-${child.personId}`}>Nombre completo</label>
            <input id={`pickup-name-${child.personId}`} name="name" required maxLength={120} />
          </div>
          <div className="field">
            <label htmlFor={`pickup-rel-${child.personId}`}>Parentesco</label>
            <input id={`pickup-rel-${child.personId}`} name="relationship" required maxLength={60} placeholder="Abuela" />
          </div>
          <Button type="submit" variant="secondary" busy={busy === 'add'}>
            Autorizar
          </Button>
        </form>
      ) : (
        <Button type="button" variant="ghost" onClick={() => setAdding(true)}>
          Autorizar a alguien más
        </Button>
      )}
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}

function CheckIn({ churchId, child, rooms, onDone }: { churchId: string; child: MyNurseryChild; rooms: NurseryRoom[]; onDone: () => void }) {
  const fits = rooms.filter((r) => child.ageMonths === null || ((r.minAgeMonths ?? 0) <= child.ageMonths && child.ageMonths <= (r.maxAgeMonths ?? 216)));
  const [roomId, setRoomId] = useState(fits[0]?.id ?? rooms[0]?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function checkIn() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/nursery/churches/${churchId}/stays`, { childId: child.personId, roomId });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  if (rooms.length === 0) return <p className="muted small">La guardería aún no tiene salas.</p>;
  return (
    <div className="checkin">
      <div className="field">
        <label htmlFor={`room-${child.personId}`}>Sala</label>
        <select id={`room-${child.personId}`} value={roomId} onChange={(e) => setRoomId(e.target.value)}>
          {rooms.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
              {fits.includes(r) ? '' : ' (no es de su edad)'}
            </option>
          ))}
        </select>
      </div>
      <Button type="button" busy={busy} onClick={checkIn}>
        Registrar entrada
      </Button>
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}
